import { useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { Button, Checkbox, Label, Textarea } from 'flowbite-react';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import Select, { MultiValue } from 'react-select';
import { Dropdown } from 'shared-utils';
import { registerForDrive } from 'src/apis/drive';
import TermsOfPlacementDrivesModal from 'src/components/recruiterPositions/recruiterTypes/Modals/TermsOfPlacementDrivesModal';
import { Drawer } from 'src/components/shared/Drawer';
import { useToast } from 'src/context/ToastProvider';
import { useGetAllDrivesQuery } from 'src/hooks/drives.hooks';
import DriveFormForRecruiter from 'src/pages/newRecruiterFlow/DriveFormForRecruiter';
import { CampusUserWithApplication, RecruiterFormData, UserRole } from 'types';

interface Props {
	jobseekers: CampusUserWithApplication[];
	showHeading?: boolean;
	onFinish?: () => void;
}

const CampusInviteJobSeekerForDrive = ({ jobseekers, onFinish, showHeading = true }: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const isMobile = useMediaQuery('(max-width:960px)');
	const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
	const [showHiringDetailsForm, setShowHiringDetailsForm] = useState<boolean>(false);
	const [hasUnregisteredDrivesSelected, setHasUnregisteredDrivesSelected] = useState<boolean>(false);
	const [formData, setFormData] = useState<{ drives: string[]; termsAccepted: boolean; remarks: string }>({
		drives: [],
		termsAccepted: false,
		remarks: '',
	});

	const { data, isLoading, refetch } = useGetAllDrivesQuery();

	const drivesOptions = data?.drives?.map(e => ({
		label: e.driveEndDate && e?.driveEndDate !== "" && e?.driveEndDate !== null
			? `${dayjs(e.driveDate).format('Do MMM')} - ${dayjs(e.driveEndDate).format('Do MMM, YYYY')} ${e.city}`
			: `${dayjs(e.driveDate).format('Do MMM, YYYY')} ${e.city}`,
		value: e._id,
	}));

	const registerForDriveFn = useMutation(['registerForDrive'], registerForDrive, {
		onSuccess(data) {
			toast({
				title: 'Drive Invite sent successfully',
				type: 'success',
			});
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			refetch();
			onFinish && onFinish();
		},
		onError(error: Error) {
			console.log('error:', error);
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (recruiterFormData?: RecruiterFormData) => {
		if (hasUnregisteredDrivesSelected && !recruiterFormData) {
			setShowHiringDetailsForm(true);
		} else {
			registerForDriveFn.mutate({
				selectedDrives: formData?.drives,
				jobseekers: jobseekers.map(e => e?._id),
				type: UserRole.RECRUITER,
				formData: recruiterFormData,
				remarks: formData?.remarks,
			});
		}
	};

	useEffect(() => {
		const unregisteredDrives = formData?.drives?.filter(
			e => !data?.drives?.find(drive => drive._id === e)?.isRegistered,
		);
		if (unregisteredDrives?.length) {
			setHasUnregisteredDrivesSelected(true);
		} else {
			setHasUnregisteredDrivesSelected(false);
		}
	}, [formData]);

	return (
		<>
			<form
				className="px-1 flex flex-col gap-[20px]"
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				{showHeading ? (
					<p className="font-inter text-[20px] font-[600] leading-[28px] mt-3">{'Placement Drive Invite '}</p>
				) : null}
				<div className="block">
					<div className="mb-[8px] flex justify-between items-end">
						<div>
							<Label>{'Drive Date And Location '}</Label>
							<span className="text-[red]">*</span>
						</div>
					</div>
					<div>
						<Select
							isMulti
							required
							isClearable
							menuPosition="fixed"
							isLoading={isLoading}
							options={drivesOptions}
							classNamePrefix="flowbite-select"
							onChange={(value: MultiValue<Dropdown>) => {
								setFormData(prev => ({ ...prev, drives: value?.map(e => e.value) }));
							}}
							value={drivesOptions?.filter(item => formData?.drives?.includes(item.value))}
						/>
					</div>
				</div>

				<div className="block">
					<div className="mb-[8px] flex justify-between items-end">
						<div>
							<Label>{'Your message '}</Label>
						</div>

						<span className="italic text-xs text-[#6B7280] ">{formData?.remarks?.length + ' / ' + 640}</span>
					</div>
					<Textarea
						name="message"
						placeholder="Write message here ..."
						value={formData?.remarks}
						maxLength={640}
						onChange={e => {
							setFormData(prev => ({ ...prev, remarks: e.target.value }));
						}}
						className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[100px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					/>
				</div>

				<div className="flex items-start gap-2 text-[#555770] text-sm font-[500]">
					<Checkbox
						color="blue"
						id="terms"
						checked={formData?.termsAccepted}
						className="mt-1 cursor-pointer"
						onChange={e => {
							setFormData(prev => ({ ...prev, termsAccepted: e.target.checked }));
						}}
					/>
					<label htmlFor="terms">
						By sending these invites, you agree to the{' '}
						<span
							className="text-[#6698FA] cursor-pointer underline"
							onClick={() => {
								setShowTermsModal(true);
							}}
						>
							terms of the placement drive.
						</span>
					</label>
				</div>
				<Button
					type="submit"
					color="purplePlaced"
					className="mx-auto w-full"
					isProcessing={registerForDriveFn.isLoading}
					disabled={registerForDriveFn.isLoading || !formData?.termsAccepted || !formData?.drives?.length}
				>
					Send Invite to Candidate
				</Button>
			</form>

			<TermsOfPlacementDrivesModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />

			<Drawer
				fullWidth={true}
				minHeight="100vh"
				isModal={!isMobile}
				isOpen={showHiringDetailsForm}
				header={
					<div className="p-5 pb-0 text-[#1F3343] font-[600] text-xl">
						<h3>Placement drive hiring details</h3>
						<p className="text-[#3568D4] font-[400] text-[14px]">
							Now that candidates have been invited, please provide a few details about the hiring process.
						</p>
					</div>
				}
				modalMaxWidth={'760px'}
				body={
					<DriveFormForRecruiter
						isModal={true}
						onFinish={formData => {
							handleSubmit(formData);
						}}
					/>
				}
				onClose={() => {
					setShowHiringDetailsForm(false);
				}}
			/>
		</>
	);
};

export default CampusInviteJobSeekerForDrive;
